import React, { useEffect, useState } from "react";
import { getDatabase, ref, onValue, remove, set } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Link } from "react-router-dom";

function YourEventsList() {

  const DEFAULT_CATEGORY = "AUTOMOTIVE";

  const [currentUserId, setCurrentUserId] = useState(null);

  // CATEGORY
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY);
  const [usersPreferedCat, getUserSelectedCategory] = useState("");
  //console.log("User preferred category:", usersPreferedCat);

  // EVENTS
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState(new Set());


  // FILTER DEFAULTS
  const FILTERDEFAULTS = {
    eventType: "all",
    hidePrevious: true,
    sortDirection: "asc",
    verifiedOnly: "",
  };

  // FILTER STATE
  const [eventTypeFilter, setEventTypeFilter] = useState(FILTERDEFAULTS.eventType);
  const [hidePrevious, setHidePrevious] = useState(FILTERDEFAULTS.hidePrevious);
  const [sortDirection, setSortDirection] = useState(FILTERDEFAULTS.sortDirection);
  const [verifiedOnly, setVerifiedOnly] = useState(FILTERDEFAULTS.verifiedOnly);

  useEffect(() => {
    if (usersPreferedCat && usersPreferedCat.trim() !== "") {
      setSelectedCategory(usersPreferedCat);
    } else {
      setSelectedCategory(DEFAULT_CATEGORY);
    }
  }, [usersPreferedCat]);


  useEffect(() => {
    const auth = getAuth();
    const db = getDatabase();

    let unsubscribeDb = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const usersRef = ref(db, `USERS/${user.uid}`);

        unsubscribeDb = onValue(usersRef, (snapshot) => {
          const data = snapshot.val();
          // console.log(data);
          if (data && data.USERPREFEREDCATEGORY) {
            getUserSelectedCategory(data.USERPREFEREDCATEGORY);
          }
        });
      } else {
        // user logged out → clean DB listener
        if (unsubscribeDb) {
          unsubscribeDb();
          unsubscribeDb = null;
        }
      }
    });

    return () => {
      // cleanup everything on unmount
      if (unsubscribeDb) unsubscribeDb();
      unsubscribeAuth();
    };
  }, []);


  // AUTH
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUserId(user.uid);
      } else {
        setCurrentUserId(null);
        setEvents([]);
        setSelectedEvents(new Set());
      }
    });
  }, []);


  
  // LOAD EVENTS
  useEffect(() => {
    if (!currentUserId) return;

    const db = getDatabase();
    const eventsRef = ref(db, `EVENTS/EVENT/${selectedCategory}`);

    const unsubscribe = onValue(eventsRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setEvents([]);
        setSelectedEvents(new Set());
        return;
      }

      const hostedEvents = Object.entries(data)
        .filter(([_, event]) => event.EVENT_OWNER === currentUserId)
        .map(([key, event]) => ({
          id: key,
          name: event.EVENT_NAME,
          date: event.EVENT_DATE,
          location: event.EVENT_LOCATION,
          postcode: event.EVENT_POSTCODE,
          price: event.EVENT_PRICE,
          public: event.EVENT_PUBLIC,
          requestaccess: event.EVENT_ATTENDBYJOINING,
          type: event.EVENT_TYPE,
          slugURL: event.EVENT_SLUG,
          urlkey: event.EVENT_URLKEY,
          verified: event.EVENT_VERIFIED === "Y", 
        }));

      setEvents(hostedEvents);
      setSelectedEvents(new Set());
    });

    return () => unsubscribe();
  }, [currentUserId, selectedCategory]);

  // FILTERED EVENTS
  const filteredEvents = events
    .filter((e) =>
      eventTypeFilter === "all" ? true : e.type === eventTypeFilter
    )
    .filter(e => {
      if (!hidePrevious || !e.date) return true;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const [day, month, year] = e.date.split("/").map(Number);
      const eventDate = new Date(year, month - 1, day); // month is 0-based

      return eventDate >= today;
    })
    .filter((e) => {
      if (verifiedOnly === "") return true;
      return e.verified;
    })
    .sort((a, b) => {
      const da = new Date(a.date);
      const db = new Date(b.date);
      return sortDirection === "asc" ? da - db : db - da;
    });

  // SELECT HANDLERS
  const toggleSelectEvent = (eventId) => {
    setSelectedEvents((prev) => {
      const updated = new Set(prev);
      updated.has(eventId) ? updated.delete(eventId) : updated.add(eventId);
      return updated;
    });
  };

  const toggleSelectAll = () => {
    if (selectedEvents.size === filteredEvents.length) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(filteredEvents.map((e) => e.id)));
    }
  };

  // DELETE
  const handleDeleteSelected = async () => {
    if (selectedEvents.size === 0) return;

    const confirmed = window.confirm(
      `Delete ${selectedEvents.size} selected event(s)? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const db = getDatabase();

      await Promise.all(
        Array.from(selectedEvents).map((eventId) => {
          const now = new Date();
          const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
          eventDeleteUpdate(selectedCategory, "Deleted", "", currentUserId, datetimenow, eventId, "Event Deleted");
          
          return remove(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}`));
        })
      );

      setSelectedEvents(new Set());
      alert("Selected events deleted successfully.");
    } catch (error) {
      console.error(error);
      alert("Failed to delete selected events.");
    }
  };
  
    function eventDeleteUpdate(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };
  return (
    <div className="mb-5">
      {/* CATEGORY */}
      <div className="row justify-content-between align-items-end mb-4">
        <div className="col-6">
          <label className="form-label">Category</label>

          <select
            className="form-select w-auto"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {usersPreferedCat && (
              <option value={usersPreferedCat}>
                Preferred ({usersPreferedCat})
              </option>
            )}
            <option value="AUTOMOTIVE">Automotive</option>
            <option value="SPORTS">Sports</option>
            <option value="MUSIC">Music</option>
          </select>
            
        </div>

        <div className="col-6 text-end">
          <button
            className="text-outline-danger"
            disabled={selectedEvents.size === 0}
            onClick={handleDeleteSelected}
          >
           Delete - {selectedEvents.size} 
          </button>
        </div>
      </div>
     
      <div className="row justify-content-between filter-section mx-0 align-items-end"></div>

      {/* FILTERS */}
      <div className="row mb-4">
        <div className="col-md-3">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
          >
            <option value="all">Show All</option>
            <option value="RACE">Race</option>
            <option value="RALLY">Rally</option>
            <option value="MEETUP">Meetup</option>
            <option value="DRAG">Drag</option>
            <option value="DRIFT">Drift</option>
            <option value="TRACKDAY">Trackday</option>
            <option value="OTHER">Other</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Previous</label>
          <select
            className="form-select"
            value={hidePrevious ? "hide" : "show"}
            onChange={(e) => setHidePrevious(e.target.value === "hide")}
          >
            <option value="hide">Upcoming Only</option>
            <option value="show">Show Previous</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Order By</label>
          <select
            className="form-select"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
          >
            <option value="asc">Closest</option>
            <option value="desc">Furthest</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Verified</label>
          <select
            className="form-select"
            value={verifiedOnly}
            onChange={(e) => setVerifiedOnly(e.target.value)}
          >
            <option value="">Any</option>
            <option value="Y">Verified Only</option>
          </select>
        </div>
      </div>
 
      {/* TABLE */}
      {filteredEvents.length === 0 ? (
        <p className="text-muted">No events match your filters.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
             
              <thead className="thead-light">
                <tr className="align-middle align-items-middle">
                  <th className="border-0 table-thead-row rounded-start">
                      <label className="custom-checkbox mb-0">
                        <input
                          type="checkbox"
                          className="custom-checkbox__input"
                          checked={
                            filteredEvents.length > 0 &&
                            selectedEvents.size === filteredEvents.length
                          }
                          onChange={toggleSelectAll}
                        />
                        <span className="custom-checkbox__box"></span>
                      </label>
                  </th>
                  <th className="border-0 table-thead-row ">Name</th>
                  <th className="border-0 table-thead-row ">Date</th>
                  <th className="border-0 table-thead-row ">Location</th>
                  <th className="border-0 table-thead-row text-center">Price</th>
                  <th className="border-0 table-thead-row text-center">Public</th>
                  <th className="border-0 table-thead-row rounded-end text-start">Type</th>
                </tr>
              </thead>
            
            <tbody>
              {filteredEvents.map((event) => (
                <tr key={event.id} className="align-middle align-items-middle">
                  <td className="p-2">
                    <label className="custom-checkbox mb-0">
                      <input
                        type="checkbox"
                        className="custom-checkbox__input"
                        checked={selectedEvents.has(event.id)}
                        onChange={() => toggleSelectEvent(event.id)}
                      />
                      <span className="custom-checkbox__box"></span>
                    </label>
                  </td>

                  <td>
                    {event.verified && (
                      <i className="bi bi-check2-circle verified-badge"></i>
                    )}  
                    <Link to={`/event/${event.urlkey}/${event.slugURL}`} target="_blank" rel="noreferrer" className="text-primary ms-2 small underline-hover"> <span  className="event-title-table">{event.name}</span>  </Link>
                  </td>

                  <td>{event.date}</td>

                  <td>
                    {event.location}, {event.postcode}
                  </td>

                  <td className="text-center">£{event.price}</td>

                  <td className="text-center">
                    {event.public === "Y" ?  <i className="bi bi-check-circle verified-badge"></i> : <i className="bi bi-x-circle text-red"></i>} 
                    {/* {event.requestaccess === "Y" ? <i className="bi bi-link-45deg verified-badge"></i> : ''} */}
                    </td>
                  <td className="text-start">
                      <i
                        className={`bi bi-circle-half me-2 ${event.type}-badge`}
                      ></i>
                      {event.type}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default YourEventsList;
